/**
 * Límite de tiempo para el arranque (guard del router).
 *
 * Cada await del proceso de arranque (initialize(), initializeFromSlug(),
 * fetchAvailableTenants()) puede quedar bloqueando la navegación hasta el
 * timeout de axios (30s) o más si la conexión se estanca. Con este límite,
 * el guard sigue en ≤ BOOT_DEADLINE_MS por el camino degradado que ya
 * maneja, y el usuario ve la app (con su banner de estado de arranque)
 * en lugar de una pantalla en blanco.
 *
 * El error lleva `code`, de modo que isTransientApiError() lo clasifique
 * como TRANSITORIO (no expulsa una sesión válida a /login).
 */

/** Piso superior para esperar el arranque completo de sesión/tenant (ms). */
export const BOOT_DEADLINE_MS = 15000

export class BootDeadlineError extends Error {
  code = 'BOOT_DEADLINE'
  name = 'BootDeadlineError'

  constructor (operation: string, ms: number) {
    super(`Arranque excedió el límite de ${Math.round(ms / 1000)}s: ${operation}`)
  }
}

/**
 * Compone la promesa contra un plazo. Si la promesa original termina después
 * del plazo, su resultado/fracaso se ignora aquí (sigue en segundo plano,
 * donde puede poblar las stores igualmente); quien gane el race decide la
 * navegación.
 */
export function withBootDeadline<T> (promise: Promise<T>, ms: number = BOOT_DEADLINE_MS, operation = 'boot'): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new BootDeadlineError(operation, ms)), ms)
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })
}
